import { BaseText } from '@/src/components/BaseText'
import { tooltipDefaultProps } from '@/src/components/Tooltip'
import { appColors } from '@/src/theme'
import { Box, type BoxProps, ButtonGroup, type ButtonGroupProps, Flex, Tooltip, useCheckbox } from '@chakra-ui/react'
import type React from 'react'
import { forwardRef, useEffect, useState } from 'react'

interface ToggleButtonProps {
  children: React.ReactNode
  isChecked: boolean
  isDisabled?: boolean
  icon?: React.ReactNode
  fullWidth?: boolean
  boxProps?: BoxProps
  height?: string
  colorScheme?: 'black' | 'blue' | 'white' | 'primary'
  dataAction?: string
  onChange: (e: React.ChangeEvent<HTMLElement>) => void
}

const checkedColors = {
  black: { color: 'white', borderColor: 'black', bgColor: 'black' },
  blue: { color: 'white', borderColor: 'accentPrimary', bgColor: 'accentPrimary' },
  primary: { color: 'primary', borderColor: 'transparent', bgColor: 'white' },
  white: { color: 'primary', borderColor: 'white', bgColor: appColors.white },
}

const ToggleButton = ({
  icon,
  children,
  isChecked,
  isDisabled,
  boxProps,
  fullWidth,
  colorScheme = 'black',
  dataAction,
  onChange,
}: ToggleButtonProps) => {
  const { getInputProps, getCheckboxProps } = useCheckbox({
    isChecked,
    onChange: isDisabled ? undefined : onChange,
  })

  const input = getInputProps()
  const checkbox = getCheckboxProps()
  const checkedStyle = checkedColors[colorScheme]
  const cursorStyle = isDisabled ? 'not-allowed' : 'pointer'

  return (
    <Box as='label' w={fullWidth ? 'full' : undefined} cursor={cursorStyle} data-action={dataAction}>
      <input {...input} style={{ ...input.style, position: 'absolute', top: '0' }} disabled={isDisabled} />

      <Box
        {...checkbox}
        cursor={cursorStyle}
        borderWidth='1px'
        borderRadius='8px'
        border='none'
        borderColor='gray.100'
        h='full'
        p='4px 8px'
        opacity={isDisabled ? 0.5 : 1}
        pointerEvents={isDisabled ? 'none' : 'auto'}
        _checked={{ ...checkedStyle, boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)' }}
        _hover={!isChecked && !isDisabled ? { borderColor: 'gray.300', bgColor: appColors.accent } : {}}
        {...boxProps}
      >
        <Flex h='full' justifyContent='center' alignItems='center' gap='4px'>
          {icon}
          {children}
        </Flex>
      </Box>
    </Box>
  )
}

export interface TabOption<T extends React.Key = React.Key> {
  label: React.ReactNode | string
  value: T
  icon?: React.ReactNode
  suffix?: React.ReactNode
  disabled?: boolean
  tooltip?: React.ReactNode
  dataAction?: string
}

export interface TabSwitcherProps<T extends React.Key = React.Key> extends Omit<ButtonGroupProps, 'onChange'> {
  options: TabOption<T>[]
  value?: T
  initialSelectedValue?: T
  fullWidth?: boolean
  height?: string
  colorScheme?: 'black' | 'blue' | 'white' | 'primary'
  onChange?: (event: React.ChangeEvent<HTMLElement>, selectedOption: TabOption<T>) => void
  fontWeight?: number | string
  fontSize?: string | number
}

function TabSwitcherInner<T extends React.Key = string>(
  {
    value,
    options,
    initialSelectedValue,
    fullWidth,
    height,
    colorScheme = 'black',
    fontWeight,
    fontSize,
    onChange,
    ...buttonGroupProps
  }: TabSwitcherProps<T>,
  ref: React.Ref<HTMLDivElement>
) {
  const [selectedValue, setSelectedValue] = useState(initialSelectedValue || options[0].value)

  const handleChange = (e: React.ChangeEvent<HTMLElement>, option: TabOption<T>) => {
    if (value === undefined) setSelectedValue(option.value)
    onChange?.(e, option)
  }

  useEffect(() => {
    if (value !== undefined) setSelectedValue(value)
  }, [value])

  return (
    <ButtonGroup
      ref={ref}
      data-testid='black-tab-switcher'
      w={fullWidth ? 'full' : 'fit-content'}
      gap='2px'
      p='3px'
      height={height ?? '32px'}
      isAttached
      bgColor='muted'
      borderRadius='8px'
      {...buttonGroupProps}
    >
      {options.map(option => {
        const tabButton = (
          <ToggleButton
            colorScheme={colorScheme}
            fullWidth={fullWidth}
            icon={option.icon}
            isChecked={selectedValue === option.value}
            isDisabled={option.disabled}
            dataAction={option.dataAction}
            onChange={e => handleChange(e, option)}
          >
            <BaseText fontSize={fontSize ?? '14px'} fontWeight={fontWeight ?? 500} alignSelf={'center'}>
              {option.label}
            </BaseText>
            {option.suffix}
          </ToggleButton>
        )

        if (option.tooltip) {
          return (
            <Tooltip key={option.value} {...tooltipDefaultProps} label={option.tooltip} placement='bottom'>
              <Box pointerEvents='auto' display='inline-block' h='full'>
                {tabButton}
              </Box>
            </Tooltip>
          )
        }

        return (
          <Box key={option.value} h='full'>
            {tabButton}
          </Box>
        )
      })}
    </ButtonGroup>
  )
}

export const TabSwitcher = forwardRef(TabSwitcherInner) as <T extends React.Key = string>(
  props: TabSwitcherProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement
